import { useEffect, useState } from 'react';
import {
  ChevronDown,
  FileText,
  LoaderCircle,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
} from 'lucide-react';
import { OperatorErrorText } from '../renderer/TechnicalDetails';
import type { PaigeDestinationsBridge } from './paige.global';
import type { PaigeCcPlan } from './product-destination-types';
import { reasonOf } from './use-content-central';
import './content-central-sheet.css';

export interface ContentCentralNewCatalogProps {
  bridge: PaigeDestinationsBridge;
  destinationId: string;
  /** The catalog was made: the destination files there now. */
  onCreated: (catalog: { id: string; name: string }) => void;
  /** Keep the current setup: nothing was made. */
  onClose: () => void;
}

/**
 * "Create a new catalog from this job" (parity E-33, CapturePoint's ContentCentralCreateWindow): what Content
 * Central would build (the catalog named after the job, its types and fields, its examples, its warnings), and
 * nothing made until the person presses Create catalog. A Create pressed again, or after a lost answer, is the
 * same plan and makes no second catalog.
 */
export function ContentCentralNewCatalog(props: ContentCentralNewCatalogProps) {
  const { plan, error } = usePlan(props.bridge, props.destinationId);
  const create = useCreate(props, plan);
  return (
    <section className="paige-cc-new" aria-label="Create a Content Central catalog">
      <header>
        <Sparkles size={18} aria-hidden="true" />
        <strong>Create a Content Central catalog</strong>
      </header>
      {!plan && !error && (
        <p className="paige-cc-state" role="status">
          <LoaderCircle className="spin" size={14} aria-hidden="true" /> Preparing a new catalog…
        </p>
      )}
      {error && <Problem title="Content Central couldn’t plan the new catalog" message={error} />}
      {plan && <PlanSummary plan={plan} />}
      {plan && <PlanNotes plan={plan} />}
      {create.message && (
        <Problem title="The catalog is not connected yet" message={create.message} />
      )}
      <CreateActions create={create} onClose={props.onClose} />
    </section>
  );
}

function CreateActions({
  create,
  onClose,
}: {
  create: ReturnType<typeof useCreate>;
  onClose: () => void;
}) {
  return (
    <div className="paige-cc-actions">
      <small>Nothing is created in Content Central until you press Create catalog.</small>
      <button type="button" className="quiet-button small" disabled={create.busy} onClick={onClose}>
        Keep the current setup
      </button>
      <button
        type="button"
        className="primary-button small"
        disabled={!create.ready}
        onClick={create.run}
      >
        {create.busy ? (
          <LoaderCircle className="spin" size={14} aria-hidden="true" />
        ) : (
          <Sparkles size={14} aria-hidden="true" />
        )}
        {create.label}
      </button>
    </div>
  );
}

function usePlan(bridge: PaigeDestinationsBridge, destinationId: string) {
  const [plan, setPlan] = useState<PaigeCcPlan | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    bridge.ccPlan?.(destinationId).then(
      (answer) => live && setPlan(answer),
      (failure: unknown) =>
        live && setError(reasonOf(failure, 'Content Central couldn’t plan the new catalog')),
    );
    return () => {
      live = false;
    };
  }, [bridge, destinationId]);
  return { plan, error };
}

/** Create: the confirmed plan applied; completed hands the catalog on, anything else says how it stands. */
function useCreate(
  { bridge, destinationId, onCreated }: ContentCentralNewCatalogProps,
  plan: PaigeCcPlan | null,
) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const run = () => {
    if (!plan?.planId || !plan.planFingerprint) return;
    setBusy(true);
    setMessage('');
    bridge
      .ccCreate?.(destinationId, plan.planId, plan.planFingerprint)
      .then((made) =>
        made.state === 'completed' && made.catalogId
          ? onCreated({ id: made.catalogId, name: made.catalogName ?? plan.catalogName })
          : setMessage(made.message ?? 'Content Central has not finished the catalog yet.'),
      )
      .catch((failure: unknown) =>
        setMessage(reasonOf(failure, 'The catalog could not be created')),
      )
      .finally(() => setBusy(false));
  };
  const ready = !busy && Boolean(plan?.planId) && (plan?.blockers.length ?? 1) === 0;
  const label = busy ? 'Creating…' : message ? 'Create again' : 'Create catalog';
  return { busy, message, run, ready, label };
}

function PlanSummary({ plan }: { plan: PaigeCcPlan }) {
  const [all, setAll] = useState(false);
  const fields = plan.documentTypes.reduce((sum, type) => sum + type.fields.length, 0);
  const shown = all ? plan.documentTypes : plan.documentTypes.slice(0, 4);
  return (
    <>
      <div className="paige-cc-new-name">
        <small>New catalog</small>
        <h3>{plan.catalogName}</h3>
        <small>Named after this job. Rename the job to change it.</small>
      </div>
      <p className="paige-cc-counts">
        <span>
          <b>1</b> new catalog
        </span>
        <span>
          <b>{plan.documentTypes.length}</b> document types
        </span>
        <span>
          <b>{fields}</b> fields
        </span>
      </p>
      <ul className="paige-cc-new-types">
        {shown.map((type) => (
          <li key={type.name}>
            <FileText size={14} aria-hidden="true" />
            <strong>{type.name}</strong>
            <small>{type.fields.map((f) => f.name).join(', ') || 'No fields'}</small>
          </li>
        ))}
      </ul>
      {plan.documentTypes.length > 4 && (
        <button type="button" className="quiet-button small" onClick={() => setAll(!all)}>
          <ChevronDown size={13} aria-hidden="true" />
          {all ? 'Show fewer' : `Show the other ${plan.documentTypes.length - 4} document types`}
        </button>
      )}
    </>
  );
}

function PlanNotes({ plan }: { plan: PaigeCcPlan }) {
  return (
    <>
      <p className="paige-cc-note">
        <ShieldCheck size={15} aria-hidden="true" />
        <span>
          Your folders and file names become the catalog&apos;s filing. Content Central shows them
          with sample values.
        </span>
      </p>
      {plan.examples.slice(0, 3).map((example) => (
        <p key={example.paigeType} className="paige-cc-path">
          <span className="paige-cc-path-type">{example.paigeType}</span>
          <span className="paige-cc-path-pieces">
            {[...example.folders, example.fileName].join(' › ')}
          </span>
        </p>
      ))}
      {plan.warnings.map((warning) => (
        <small key={warning} className="paige-cc-warning">
          {warning}
        </small>
      ))}
      {plan.blockers.map((blocker) => (
        <Problem key={blocker} title="Fix this first" message={blocker} />
      ))}
    </>
  );
}

function Problem({ title, message }: { title: string; message: string }) {
  return (
    <div className="paige-cc-note is-bad" role="alert">
      <TriangleAlert size={16} aria-hidden="true" />
      <span>
        <strong>{title}</strong>
        <OperatorErrorText message={message} />
      </span>
    </div>
  );
}
